// Pure LOD / request-planning logic, kept free of WebGL so it can be unit-tested.

/** Bytes per point record (prep/cvformat.py). A version 1 build has 12 (no capture range): use its index's `record`. */
export const RECORD = 14;
export const RANGE_STEP = 0.02;   // m per unit of the record's range byte (0: unknown)

export interface ChunkIndex {
  origin: [number, number, number];
  size: [number, number, number];
  levelCounts: number[];
  offsets: number[];
  spacing?: number[];   // typical spacing when levels 0..l are drawn (prep estimate)
}

/** Exponential ease of cur toward target over dt seconds (time constant tau). */
export function ease(cur: number, target: number, dt: number, tau: number) {
  return target + (cur - target) * Math.exp(-Math.max(dt, 0) / tau);
}

/** Levels a chunk draws: every loaded level (at least level 0's slot). */
export const drawLevels = (loaded: number) => Math.max(1, loaded);

/** Points in levels [0, levels). */
export function prefixPoints(counts: number[], levels: number) {
  let n = 0;
  for (let i = 0; i < levels && i < counts.length; i++) n += counts[i];
  return n;
}

/**
 * How many levels a chunk needs so the point spacing projects to at most
 * targetPx pixels. pxPerM is pixels per metre at distance 1 (perspective) or
 * per metre (ortho, distance 1). Always at least 1 (level 0).
 */
export function levelsWanted(spacing: number[], distance: number, pxPerM: number, targetPx: number) {
  const maxSpacing = (targetPx * Math.max(distance, 1e-3)) / pxPerM;
  for (let l = 0; l < spacing.length; l++) if (spacing[l] <= maxSpacing) return l + 1;
  return spacing.length;
}

export interface Want {
  key: number;       // caller's id for the chunk
  file: number;      // which .bin
  chunk: number;     // chunk index in that file (file order)
  level: number;
  offset: number;    // byte offset of this chunk's level in the file
  bytes: number;
  priority: number;
}

export interface RangeReq {
  file: number;
  level: number;
  start: number;
  end: number;       // exclusive
  parts: Want[];
  priority: number;
}

/**
 * Take the highest-priority wants and merge each with wants for the same file
 * and level whose bytes are contiguous with it (adjacent chunks), up to
 * maxBytes per request (level 0 is never split). Returns at most maxReqs.
 */
export function planRequests(wants: Want[], maxReqs: number, maxBytes: number): RangeReq[] {
  const sorted = [...wants].sort((a, b) => b.priority - a.priority);
  const byPos = new Map<string, Want>(), byEnd = new Map<string, Want>();
  for (const w of wants) if (w.bytes > 0) {
    byPos.set(`${w.file}:${w.offset}`, w);
    byEnd.set(`${w.file}:${w.offset + w.bytes}`, w);
  }
  const used = new Set<Want>();
  const out: RangeReq[] = [];
  for (const w of sorted) {
    if (out.length >= maxReqs) break;
    if (used.has(w)) continue;
    used.add(w);
    const r: RangeReq = { file: w.file, level: w.level, start: w.offset, end: w.offset + w.bytes, parts: [w], priority: w.priority };
    const cap = w.level === 0 ? Infinity : maxBytes;
    // extend forwards
    for (;;) {
      const n = byPos.get(`${w.file}:${r.end}`);
      if (!n || used.has(n) || n.level !== w.level || r.end - r.start + n.bytes > cap) break;
      used.add(n); r.parts.push(n); r.end += n.bytes;
    }
    // and backwards
    for (;;) {
      const p = byEnd.get(`${w.file}:${r.start}`);
      if (!p || used.has(p) || p.level !== w.level || r.end - r.start + p.bytes > cap) break;
      used.add(p); r.parts.unshift(p); r.start = p.offset;
    }
    out.push(r);
  }
  return out;
}

export interface Resident {
  key: number;
  levels: number;       // loaded levels
  needed: number;       // levels wanted right now
  counts: number[];
  lastUsed: number;     // frame number
  priority: number;
}

/**
 * Choose finest levels to drop so `need` more points fit in `budget`.
 * Never drops level 0. Prefers levels nobody needs now, then least recently
 * used, then lowest priority; only drops a needed level if its priority is
 * below `forPriority`. Returns [key, levelsAfter] pairs, or null if it can't fit.
 */
export function chooseEvictions(res: Resident[], loaded: number, need: number, budget: number, forPriority: number) {
  if (loaded + need <= budget) return [];
  const st = res.map(r => ({ ...r }));
  const out = new Map<number, number>();
  let free = loaded + need - budget;
  for (;;) {
    let best: (typeof st)[0] | null = null, bestScore = Infinity;
    for (const r of st) {
      if (r.levels <= 1) continue;
      const spare = r.levels > r.needed;
      if (!spare && r.priority >= forPriority) continue;
      const score = (spare ? 0 : 1e12) + r.lastUsed * 1e3 + r.priority;
      if (score < bestScore) { bestScore = score; best = r; }
    }
    if (!best) return null;
    best.levels--;
    free -= best.counts[best.levels];
    out.set(best.key, best.levels);
    if (free <= 0) return [...out.entries()];
  }
}

/**
 * A camera image's mesh (its depth grid back-projected, Viewer ImageMesh): a world bounding sphere over the pixels
 * with depth (c, r) and the size of a depth pixel there (cell: median depth / fx, m). model: camera to world
 * (column-major); K: fx, fy, cx, cy. Null when no pixel has depth.
 */
export function imageBounds(depth: Float32Array, dw: number, dh: number, K: number[], model: ArrayLike<number>): { c: [number, number, number]; r: number; cell: number } | null {
  const m = model, pts: number[] = [], zs: number[] = [];
  const S = Math.max(1, Math.floor(Math.min(dw, dh) / 32));   // a sample of the pixels (and the last row and column) is enough
  const at = (n: number) => { const a: number[] = []; for (let i = 0; i < n - 1; i += S) a.push(i); a.push(n - 1); return a; };
  const xs = at(dw);
  for (const y of at(dh)) for (const x of xs) {
    const z = depth[y * dw + x];
    if (!(z > 0)) continue;
    const cx = (x - K[2]) / K[0] * z, cy = (y - K[3]) / K[1] * z;
    pts.push(m[0] * cx + m[4] * cy + m[8] * z + m[12], m[1] * cx + m[5] * cy + m[9] * z + m[13], m[2] * cx + m[6] * cy + m[10] * z + m[14]);
    zs.push(z);
  }
  if (!zs.length) return null;
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < pts.length; i += 3) for (let k = 0; k < 3; k++) { lo[k] = Math.min(lo[k], pts[i + k]); hi[k] = Math.max(hi[k], pts[i + k]); }
  const c: [number, number, number] = [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2, (lo[2] + hi[2]) / 2];
  let r = 0;
  for (let i = 0; i < pts.length; i += 3) r = Math.max(r, Math.hypot(pts[i] - c[0], pts[i + 1] - c[1], pts[i + 2] - c[2]));
  zs.sort((a, b) => a - b);
  // a few cm more: the pixels between the samples, and the pull towards the viewer (shaders.ts IMG_VS)
  return { c, r: r + 0.1 + 0.02 * zs[zs.length - 1], cell: zs[zs.length >> 1] / K[0] };
}

/** Image mesh strides: every depth pixel, or every 2nd, 4th... (Viewer draws a coarser grid when it is small on screen). */
export const IMAGE_STEPS = [1, 2, 4, 8, 16];

/**
 * The coarsest stride whose grid cell (cell m per depth pixel, times the stride) still projects to at most targetPx
 * pixels at distance (pxPerM: pixels per metre at distance 1, or per metre in ortho with distance 1). At least 1.
 */
export function imageStep(cell: number, distance: number, pxPerM: number, targetPx: number) {
  const px = cell * pxPerM / Math.max(distance, 1e-3);
  let step = 1;
  for (const s of IMAGE_STEPS) if (s * px <= targetPx) step = s;
  return step;
}
